import { Card, Progress, Tag } from "antd";
import type { ReactNode } from "react";

interface UsageCardProps {
  readonly title: string;
  readonly used: number;
  readonly limit: number | undefined;
  readonly color: string;
  readonly tileClassName?: string;
  readonly icon?: ReactNode;
  readonly subtitle?: string;
}

export function UsageCard({
  title,
  used,
  limit,
  color,
  tileClassName = "tile-accent-indigo",
  icon,
  subtitle,
}: UsageCardProps) {
  const isUnlimited = !limit || limit <= 0;
  const percent = isUnlimited
    ? 0
    : Math.min(100, Math.round((used / (limit as number)) * 100));
  const remaining = isUnlimited ? 0 : Math.max(0, (limit as number) - used);

  return (
    <Card className={`crow-card h-full ${tileClassName}`} bordered>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-content-tertiary">
            {title}
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-4xl font-bold text-content-primary leading-none">
              {used.toLocaleString()}
            </span>
            {!isUnlimited && (
              <span className="text-sm text-content-secondary">
                / {(limit as number).toLocaleString()}
              </span>
            )}
          </div>
          {subtitle && (
            <div className="text-xs text-content-secondary mt-1">
              {subtitle}
            </div>
          )}
        </div>
        {icon && (
          <div
            className="h-11 w-11 rounded-arc-md grid place-items-center text-xl"
            style={{
              background: color,
              color: "#fff",
              boxShadow: `0 6px 16px -6px ${color}`,
            }}
          >
            {icon}
          </div>
        )}
      </div>

      {isUnlimited ? (
        <Tag color="gold" className="!rounded-full !px-3 !py-0.5 !text-xs">
          Unlimited
        </Tag>
      ) : (
        <>
          <Progress
            percent={percent}
            strokeColor={color}
            trailColor="rgba(17,24,39,0.06)"
            showInfo={false}
          />
          <div className="flex justify-between mt-2 text-xs">
            <span className="text-content-secondary font-medium">
              {percent}% used
            </span>
            <span className="text-content-tertiary">
              {remaining.toLocaleString()} remaining
            </span>
          </div>
        </>
      )}
    </Card>
  );
}
